/**
 * usePopup — Promise 기반 모달 스택 훅 (Zustand store).
 *
 * 【역할】
 * - `const result = await openPopup(Comp, props)` 한 줄로 모달 라이프사이클 캡슐화.
 * - 선택/확인 결과를 resolver 로 되돌려 호출부 비즈니스 플로우를 선형 코드로 유지.
 *
 * 【주요 기능】
 * - openPopup(Component, props): Promise<TResult | null>
 * - Component 에 주입되는 props: { popupProps, close(result?) }
 * - usePopupStack(): 현재 열린 popup 배열 구독 (ModalHost 렌더링용)
 *
 * 【Promise 기반 스택 구조 (L4)】
 * - store.stack: PopupEntry[] — FIFO 배열로 push/remove.
 * - openPopup 호출 시:
 *     1) popup_{seq++} id 발급
 *     2) stack 에 { id, Component, props, resolve } push
 *     3) new Promise 의 resolve 를 entry.resolve 에 위임
 *     4) 모달 컴포넌트는 주입받은 close(result) 를 호출 → entry.resolve(result) + remove(id)
 * - 여러 모달이 중첩될 수 있으며, stack 순서대로 렌더링 → z-index 는 antd Modal 내부 처리.
 *
 * 【사용 예시】
 * ```tsx
 * const { openPopup } = usePopup()
 * const result = await openPopup<UserPick>(UserPickerModal, { deptId: 1 })
 * if (result) setSelectedUser(result)
 *
 * // UserPickerModal 내부:
 * function UserPickerModal({ popupProps, close }: PopupComponentProps<UserPick, { deptId: number }>) {
 *   return <Button onClick={() => close({ userId: 'u1' })}>선택</Button>
 * }
 * ```
 *
 * 【왜 이 패턴?】
 * - useState + 조건부 렌더링 방식은 여러 페이지에서 모달 상태 관리를 매번 반복.
 * - Promise 기반 imperative API 로 "호출 → 결과 대기 → 분기" 를 async/await 자연스럽게 작성.
 * - ModalHost 컴포넌트가 store 를 구독하여 실제 렌더링을 담당 (관심사 분리).
 */
import { ComponentType } from 'react'
import { create } from 'zustand'

/** 스택에 보관되는 popup 엔트리 — ModalHost 가 이 엔트리를 렌더링. */
export interface PopupEntry<TResult = unknown, TProps = unknown> {
  id: string
  Component: ComponentType<PopupComponentProps<TResult, TProps>>
  props: TProps
  resolve: (result: TResult | null) => void
}

/**
 * popup 으로 지정된 Component 가 받는 props.
 * - popupProps: openPopup 호출 시 전달한 사용자 props (읽기 전용)
 * - close: 결과 전달 + 모달 닫기. undefined/null 전달 시 cancel 로 해석.
 */
export interface PopupComponentProps<TResult = unknown, TProps = unknown> {
  popupProps: TProps
  close: (result?: TResult | null) => void
}

/** 전역 popup 스택 store — ModalHost 가 stack 을 구독. */
interface PopupState {
  stack: PopupEntry[]
  push: (entry: PopupEntry) => void
  remove: (id: string) => void
}

const usePopupStore = create<PopupState>((set) => ({
  stack: [],
  push: (entry) => set((s) => ({ stack: [...s.stack, entry] })),
  remove: (id) => set((s) => ({ stack: s.stack.filter((e) => e.id !== id) }))
}))

let popupSeq = 0

/**
 * usePopup — popup 오픈 진입점.
 * @returns { openPopup } — 호출 시 Promise 반환, close 호출 시 resolve.
 */
export function usePopup() {
  const push = usePopupStore((s) => s.push)
  const remove = usePopupStore((s) => s.remove)

  /**
   * 모달 컴포넌트를 스택에 push 하고 결과를 기다리는 Promise 반환.
   * @param Component - 렌더링할 모달 (PopupComponentProps 시그니처).
   * @param props - popupProps 로 주입될 사용자 데이터.
   * @returns 모달이 close(result) 로 닫힐 때 resolve 되는 Promise.
   */
  function openPopup<TResult = unknown, TProps = unknown>(
    Component: ComponentType<PopupComponentProps<TResult, TProps>>,
    props: TProps
  ): Promise<TResult | null> {
    return new Promise<TResult | null>((resolve) => {
      const id = `popup_${++popupSeq}`
      // generic 보존을 위해 PopupEntry 캐스트 필요 — push API 는 unknown 으로 normalize 됨
      const entry: PopupEntry<TResult, TProps> = {
        id,
        Component,
        props,
        resolve: (r) => {
          resolve(r)
          remove(id)
        }
      }
      push(entry as unknown as PopupEntry)
    })
  }

  return { openPopup }
}

/**
 * 현재 열린 popup 스택 구독 — ModalHost 전용.
 * 일반 페이지 코드에서는 사용하지 않음.
 */
export function usePopupStack() {
  return usePopupStore((s) => s.stack)
}

export { usePopupStore }
